/**
 * Generic Wizard Context
 * Extracted from KYCWizardContext and generified for reuse
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { WizardConfig, WizardContextValue } from './types';
import { useDebounce } from '../../hooks/useDebounce';
import { toast } from 'sonner';

// Create generic context
const WizardContext = createContext<WizardContextValue<any> | undefined>(undefined);

/**
 * Hook to access wizard context
 * Must be used within WizardProvider
 */
export const useWizardContext = <T = any,>() => {
  const context = useContext(WizardContext) as WizardContextValue<T> | undefined;
  if (!context) {
    throw new Error('useWizardContext must be used within WizardProvider');
  }
  return context;
};

// Provider props
interface WizardProviderProps<T = any> {
  children: ReactNode;
  clientId: string;
  clientName: string;
  config: WizardConfig<T>;
  initialData?: Partial<T>;
  reviewMode?: boolean;
}

/**
 * Generic Wizard Provider
 * Manages state and logic for multi-step form wizards
 */
export const WizardProvider = <T = any,>({
  children,
  clientId,
  clientName,
  config,
  initialData,
  reviewMode = false
}: WizardProviderProps<T>) => {
  // Initialize React Hook Form
  const methods = useForm<Partial<T>>({
    defaultValues: initialData as any || ({} as Partial<T>),
    mode: 'onChange'
  });

  // Step state - start at step 2 in review mode if configured
  const initialStep = reviewMode && config.features.reviewMode ? 2 : 1;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [reviewedSteps, setReviewedSteps] = useState<Set<number>>(new Set());

  // Form data state
  const [formData, setFormData] = useState<Partial<T>>(initialData || ({} as Partial<T>));

  /**
   * Reset form when initialData changes
   * This ensures form fields are populated when data is loaded from API
   */
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      console.log('🔄 [WizardContext] Resetting form with initialData:', Object.keys(initialData));
      methods.reset(initialData as any);
      setFormData(initialData);
    }
  }, [initialData, methods]);

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [enableAutoSave, setEnableAutoSave] = useState(config.features.autoSave || false);

  // Debounced form data for auto-save
  const debouncedFormData = useDebounce(formData, 3000);

  /**
   * Update form data for a specific section
   */
  const updateFormData = useCallback((section: keyof T, data: any) => {
    setFormData(prev => {
      // Handle arrays directly
      if (Array.isArray(data)) {
        return {
          ...prev,
          [section]: data
        };
      }

      // Handle objects with deep merge
      return {
        ...prev,
        [section]: {
          ...(prev[section] as any || {}),
          ...data
        }
      };
    });
    setIsDirty(true);
  }, []);

  /**
   * Reset form data to initial state
   */
  const resetFormData = useCallback(() => {
    setFormData(initialData || ({} as Partial<T>));
    setIsDirty(false);
  }, [initialData]);

  /**
   * Mark step as complete
   */
  const markStepComplete = useCallback((step: number) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  }, []);

  /**
   * Mark step as reviewed (for review mode)
   */
  const markStepReviewed = useCallback((step: number) => {
    setReviewedSteps(prev => new Set([...prev, step]));
  }, []);

  /**
   * Check if step is complete
   */
  const isStepComplete = useCallback((step: number): boolean => {
    return completedSteps.has(step);
  }, [completedSteps]);

  /**
   * Check if step is reviewed
   */
  const isStepReviewed = useCallback((step: number): boolean => {
    return reviewedSteps.has(step);
  }, [reviewedSteps]);

  /**
   * Check if can navigate to step
   */
  const canNavigateToStep = useCallback((step: number): boolean => {
    // Can always go to previous steps or current step
    if (step <= currentStep) return true;

    // Can go to completed steps
    if (completedSteps.has(step - 1)) return true;

    // Allow free navigation in review mode
    if (reviewMode) return true;

    // For now, allow navigation (can be made stricter later)
    return true;
  }, [currentStep, completedSteps, reviewMode]);

  /**
   * Navigate to next step
   */
  const goToNextStep = useCallback(async () => {
    // Validate current step if validation is enabled
    if (config.features.stepValidation && config.validateStep) {
      const isValid = await config.validateStep(currentStep, formData);
      if (!isValid) {
        toast.error('Please complete all required fields', {
          duration: 4000
        });
        return;
      }
    }

    // Mark step as completed/reviewed
    if (reviewMode) {
      markStepReviewed(currentStep);
    } else {
      markStepComplete(currentStep);
    }

    // Navigate to next step
    if (currentStep < config.steps.length) {
      setCurrentStep(currentStep + 1);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep, formData, config, reviewMode, markStepComplete, markStepReviewed]);

  /**
   * Navigate to previous step
   */
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentStep]);

  /**
   * Navigate to specific step
   */
  const goToStep = useCallback((step: number) => {
    if (canNavigateToStep(step)) {
      setCurrentStep(step);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [canNavigateToStep]);

  /**
   * Save progress
   */
  const saveProgress = useCallback(async (manual = false) => {
    if (!config.onSave) {
      console.warn('No onSave callback provided to wizard config');
      return;
    }

    setIsSaving(true);
    try {
      // Save to localStorage
      const storageKey = `${config.formId}_draft_${clientId}`;
      localStorage.setItem(storageKey, JSON.stringify({
        formData,
        completedSteps: Array.from(completedSteps),
        reviewedSteps: Array.from(reviewedSteps),
        currentStep,
        savedAt: new Date().toISOString()
      }));

      // Call config onSave callback
      await config.onSave(formData);

      setLastSavedAt(new Date());
      setIsDirty(false);

      if (manual) {
        toast.success('Progress saved', {
          duration: 2000,
          position: 'bottom-right'
        });
      }
    } catch (error) {
      console.error('Failed to save progress:', error);
      if (manual) {
        toast.error('Failed to save progress', {
          description: 'Your changes may not be saved. Please try again.',
          duration: 4000
        });
      }
    } finally {
      setIsSaving(false);
    }
  }, [clientId, config, formData, completedSteps, reviewedSteps, currentStep]);

  /**
   * Submit form
   */
  const submitForm = useCallback(async () => {
    if (!config.onComplete) {
      console.warn('No onComplete callback provided to wizard config');
      return;
    }

    setIsSaving(true);
    try {
      // In review mode, check if all steps reviewed
      if (reviewMode) {
        const stepsToReview = config.steps
          .filter(s => s.id !== 1) // Skip first step if document upload
          .map(s => s.id);

        const allReviewed = stepsToReview.every(step => reviewedSteps.has(step));

        if (!allReviewed) {
          toast.error('Please review all sections before submitting', {
            description: 'Navigate through each step to confirm you have reviewed the data',
            duration: 5000
          });
          setIsSaving(false);
          return;
        }
      } else {
        // Validate all steps in normal mode
        if (config.features.stepValidation && config.validateStep) {
          for (let i = 1; i <= config.steps.length; i++) {
            const isValid = await config.validateStep(i, formData);
            if (!isValid) {
              setCurrentStep(i);
              toast.error(`Please complete step ${i}: ${config.steps[i - 1].name}`, {
                duration: 4000
              });
              setIsSaving(false);
              return;
            }
          }
        }
      }

      // Submit form
      await config.onComplete(formData as T);

      // Clear localStorage
      const storageKey = `${config.formId}_draft_${clientId}`;
      localStorage.removeItem(storageKey);

      toast.success(`${config.formTitle} submitted successfully!`, {
        duration: 5000,
        position: 'bottom-right'
      });
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('Failed to submit form. Please try again.', {
        duration: 4000
      });
    } finally {
      setIsSaving(false);
    }
  }, [clientId, config, formData, reviewMode, reviewedSteps]);

  /**
   * Auto-save effect
   */
  useEffect(() => {
    if (enableAutoSave && isDirty && !isSaving && config.onSave) {
      saveProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData, enableAutoSave, isDirty, isSaving]);

  /**
   * Load saved draft on mount
   */
  useEffect(() => {
    if (initialData) return;

    const storageKey = `${config.formId}_draft_${clientId}`;
    const savedData = localStorage.getItem(storageKey);

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData(parsed.formData || {});
        setCompletedSteps(new Set(parsed.completedSteps || []));
        setReviewedSteps(new Set(parsed.reviewedSteps || []));
        setCurrentStep(parsed.currentStep || initialStep);

        toast.info('Draft loaded from local storage', {
          duration: 3000,
          position: 'bottom-right'
        });
      } catch (error) {
        console.error('Failed to load saved draft:', error);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, config.formId, initialData]);

  // Context value
  const value: WizardContextValue<T> = {
    // Config
    config,

    // State
    clientId,
    clientName,
    currentStep,
    totalSteps: config.steps.length,
    completedSteps,
    reviewedSteps,
    formData,
    isDirty,
    isSaving,
    lastSavedAt,
    reviewMode,
    enableAutoSave,

    // Navigation
    goToStep,
    goToNextStep,
    goToPreviousStep,
    canNavigateToStep,

    // Data
    updateFormData,
    resetFormData,

    // Save/submit
    saveProgress,
    submitForm,

    // Completion tracking
    markStepComplete,
    markStepReviewed,
    isStepComplete,
    isStepReviewed,

    // Settings
    setEnableAutoSave
  };

  return (
    <WizardContext.Provider value={value}>
      <FormProvider {...methods}>
        {children}
      </FormProvider>
    </WizardContext.Provider>
  );
};
